class CartNotification extends HTMLElement {
  connectedCallback() {
    // Move the drawer to the end of <body> so `position: fixed` resolves
    // against the viewport, not against the transformed sticky header.
    // appendChild re-fires connectedCallback, so bail out and let the
    // second pass do the setup.
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }
 
    if (this.tlInitialized) return;
    this.tlInitialized = true;
 
    this.init();
  }
 
  init() {
    this.notification =
      this.querySelector('#cart-notification') ||
      document.getElementById('cart-notification');
 
    if (!this.notification) return;
 
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onCartIconClick =
      this.handleCartIconClick.bind(this);
 
    this.notification.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close()
    );
 
    const closeButton = this.querySelector(
      '.cart-notification__close'
    );
 
    const continueButton = this.querySelector(
      '.cart-notification__links .button-label'
    );
 
    closeButton?.addEventListener(
      'click',
      this.close.bind(this)
    );
 
    continueButton?.addEventListener(
      'click',
      this.close.bind(this)
    );
 
    document.addEventListener(
      'click',
      this.onCartIconClick
    );
  }
 
  open() {
    if (!this.notification) return;
 
    this.notification.scrollTop = 0;
 
    const scrollArea = this.notification.querySelector(
      '.tl-notification-scroll'
    );
 
    if (scrollArea) {
      scrollArea.scrollTop = 0;
    }
 
    this.notification.classList.add(
      'animate',
      'active'
    );
 
    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );
 
    document.body.addEventListener(
      'click',
      this.onBodyClick
    );
  }
 
  close() {
    if (!this.notification) return;
 
    this.notification.classList.remove('active');
 
    document.body.removeEventListener(
      'click',
      this.onBodyClick
    );
 
    removeTrapFocus(this.activeElement);
  }
 
  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
 
    this.getSectionsToRender().forEach((section) => {
      const target =
        document.getElementById(section.id);
 
      const sectionHTML =
        parsedState.sections?.[section.id];
 
      if (!target || !sectionHTML) return;
 
      const innerHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );
 
      if (innerHTML !== null) {
        target.innerHTML = innerHTML;
      }
    });
 
    const message = document.getElementById(
      'CartNotification-Message'
    );
 
    if (message) {
      message.textContent =
        'Item added to your cart';
    }
 
    if (this.header) {
      this.header.reveal();
    }
 
    this.open();
    this.refreshCart();
  }
 
  async refreshCart() {
    try {
      const root =
        window.Shopify?.routes?.root || '/';
 
      const response = await fetch(`${root}cart.js`, {
        headers: {
          Accept: 'application/json',
        },
        credentials: 'same-origin',
      });
 
      if (!response.ok) {
        throw new Error('Unable to load cart');
      }
 
      const cart = await response.json();
 
      document.dispatchEvent(
        new CustomEvent(
          'tl:cart-notification:cart',
          {
            detail: {
              cart,
            },
          }
        )
      );
 
      return cart;
    } catch (error) {
      console.error(
        'Cart notification could not refresh:',
        error
      );
 
      return null;
    }
  }
 
  async handleCartIconClick(evt) {
    const cartTrigger = evt.target.closest(
      '#cart-icon-bubble, .header__icon--cart'
    );
 
    if (
      !cartTrigger ||
      cartTrigger.closest('cart-notification')
    ) {
      return;
    }
 
    evt.preventDefault();
    this.setActiveElement(cartTrigger);
 
    const message = document.getElementById(
      'CartNotification-Message'
    );
 
    if (message) {
      message.textContent = 'Your cart';
    }
 
    this.open();
    await this.refreshCart();
  }
 
  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }
 
  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );
 
    const selectedElement =
      parsedDocument.querySelector(selector);
 
    return selectedElement
      ? selectedElement.innerHTML
      : null;
  }
 
  handleBodyClick(evt) {
    const target = evt.target;
 
    if (
      target !== this.notification &&
      !target.closest('cart-notification')
    ) {
      const disclosure = target.closest(
        'details-disclosure, header-menu'
      );
 
      this.activeElement = disclosure
        ? disclosure.querySelector('summary')
        : null;
 
      this.close();
    }
  }
 
  setActiveElement(element) {
    this.activeElement = element;
  }
}
 
if (!customElements.get('cart-notification')) {
  customElements.define(
    'cart-notification',
    CartNotification
  );
}